import { useCallback, forwardRef, useEffect, useState } from 'react'
import { FloatingInput, FloatingInputProps } from '@icanbwell/ui-toolkit'
import { FieldError, UseFormReturn, useWatch } from 'react-hook-form'
import { Typography, Box } from '@mui/material'
import * as Yup from 'yup'
import PasswordRequirement from './PasswordRequirement'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import debounce from 'debounce-promise'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { useValidateCommonPasswordMutation } from '@client-hub/store/api/crs/react'
import { useTranslation } from 'react-i18next'

export const newPasswordErrorTypes = {
  minimumLength: 'minimumLength',
  atLeastOneLower: 'atLeastOneLower',
  atLeastOneUpper: 'atLeastOneUpper',
  atLeastOneNumber: 'atLeastOneNumber',
  atLeastOneSpecial: 'atLeastOneSpecial',
  notCommonPassword: 'notCommonPassword',
}
const DEBOUNCE_TIME = 150

export const useSchema = () => {
  const [validateCommonPassword] = useValidateCommonPasswordMutation()
  const isCommonPassword = useCallback(
    debounce(async (password: string) => {
      try {
        await validateCommonPassword({ password }).unwrap()
        return false
      } catch (error) {
        return (error as FetchBaseQueryError).status === 400
      }
    }, DEBOUNCE_TIME),
    [],
  )
  return (
    Yup.string()
      .max(30)
      // NOTE: Leaving error messages below as empty strings as they're handled via
      // the `PasswordRequirement` component.
      .test(newPasswordErrorTypes.minimumLength, '', (value) => (value ? value.length >= 8 : true))
      .test(newPasswordErrorTypes.atLeastOneLower, '', (value) => (value ? !!value.match('(?=.*[a-z])') : true))
      .test(newPasswordErrorTypes.atLeastOneUpper, '', (value) => (value ? !!value.match('(?=.*[A-Z])') : true))
      .test(newPasswordErrorTypes.atLeastOneNumber, '', (value) => (value ? !!value.match('(?=.*[0-9])') : true))
      .test(newPasswordErrorTypes.atLeastOneSpecial, '', (value) =>
        value ? !!value.match('(?=.*[!@#$^&*()_+=])') : true,
      )
      .test(newPasswordErrorTypes.notCommonPassword, '', async (value) =>
        value ? !(await isCommonPassword(value)) : true,
      )
  )
}

export type NewPasswordData = Yup.TypeOf<ReturnType<typeof useSchema>>

export type Props = Omit<FloatingInputProps, 'ref'> & {
  form: UseFormReturn<any>
  name: string
}

// TODO (1): Update this component once RHF has better support for generic forms.
// See: https://github.com/react-hook-form/react-hook-form/discussions/7199
// TODO (2): Need to add a loading indicator of some sort while the common
// password check is running.

const PasswordField = forwardRef<HTMLInputElement, Props>(
  (
    {
      form: {
        control,
        formState: { errors },
        trigger,
      },
      name,
      ...rest
    },
    ref,
  ) => {
    // Without this state variable the password requirments will show successful on initial input.
    const [initialValidationRan, setInitialValidationRan] = useState(false)
    const { t } = useTranslation('shared', { keyPrefix: 'forms.fields.newPassword' })
    const inputValue = useWatch({ control, name })
    // TODO: Highly unlikely but this may become a perf issue as this runs alongside
    // RHF own validation once it kicks in (causing duplicate validation processes).
    const runValidation = useCallback(
      debounce(async () => {
        await trigger(name)
        if (!initialValidationRan) setInitialValidationRan(true)
      }, DEBOUNCE_TIME),
      [],
    )

    useEffect(() => {
      if (inputValue) runValidation()
    }, [inputValue])

    // react-hook-form types `errors.password` as a union of FieldError/Merge/MultipleFieldErrors
    // shapes when the form is typed `UseFormReturn<any>`; at runtime it's always a plain FieldError here.
    const passwordError = errors.password as FieldError | undefined

    return (
      <>
        <FloatingInput {...rest} ref={ref} name={name} type="password" />
        {!!inputValue && initialValidationRan && passwordError && (
          <Box
            sx={{
              borderWidth: '2px',
              borderColor: 'success.600',
              borderStyle: 'solid',
              backgroundColor: 'success.50',
              padding: '1rem',
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>Password must meet the following conditions:</Typography>
            <PasswordRequirement
              error={!!(passwordError?.types?.[newPasswordErrorTypes.minimumLength] || passwordError?.types?.max)}
            >
              {t('validation.minAndMax')}
            </PasswordRequirement>
            <PasswordRequirement error={!!passwordError?.types?.[newPasswordErrorTypes.atLeastOneUpper]}>
              {t('validation.oneUpperCaseChar')}
            </PasswordRequirement>
            <PasswordRequirement error={!!passwordError?.types?.[newPasswordErrorTypes.atLeastOneLower]}>
              {t('validation.oneLowerCaseChar')}
            </PasswordRequirement>
            <PasswordRequirement error={!!passwordError?.types?.[newPasswordErrorTypes.atLeastOneNumber]}>
              {t('validation.oneNumber')}
            </PasswordRequirement>
            <PasswordRequirement error={!!passwordError?.types?.[newPasswordErrorTypes.atLeastOneSpecial]}>
              {t('validation.oneSpecial')}
            </PasswordRequirement>
            <PasswordRequirement error={!!passwordError?.types?.[newPasswordErrorTypes.notCommonPassword]}>
              {t('validation.notCommon')}
            </PasswordRequirement>
          </Box>
        )}
      </>
    )
  },
)

export default PasswordField
